import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { themes, applyTheme, defaultTheme } from '../lib/themes';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export function ThemeProvider({ children }) {
  const [customThemes, setCustomThemes] = useState({});
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme');
    
    // Load custom themes
    const storedCustomThemes = localStorage.getItem('customThemes');
    if (storedCustomThemes) {
      const customThemesData = JSON.parse(storedCustomThemes);
      if (savedTheme && (themes[savedTheme] || customThemesData[savedTheme])) {
        return savedTheme;
      }
    } else if (savedTheme && themes[savedTheme]) {
      return savedTheme;
    }
    
    return defaultTheme;
  });

  // Load custom themes on mount
  useEffect(() => {
    const storedCustomThemes = localStorage.getItem('customThemes');
    if (storedCustomThemes) {
      setCustomThemes(JSON.parse(storedCustomThemes));
    }
  }, []);

  useEffect(() => {
    const allThemes = { ...themes, ...customThemes };
    const theme = allThemes[currentTheme];
    if (theme) {
      applyTheme(theme);
    
    // Add or remove dark class for Tailwind
    if (theme.prefersDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    }
    
    // Save to localStorage
    localStorage.setItem('theme', currentTheme);
  }, [currentTheme, customThemes]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      const savedTheme = localStorage.getItem('theme');
      // Only auto-switch if user hasn't manually set a theme
      if (!savedTheme) {
        setCurrentTheme(e.matches ? 'dark' : 'light');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setCurrentTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const allThemes = { ...themes, ...customThemes };

  // Function to reload custom themes from localStorage
  const reloadCustomThemes = () => {
    const storedCustomThemes = localStorage.getItem('customThemes');
    if (storedCustomThemes) {
      setCustomThemes(JSON.parse(storedCustomThemes));
    }
  };

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    theme: currentTheme,
    setTheme: setCurrentTheme,
    toggleTheme,
    themes: Object.keys(allThemes),
    currentThemeData: allThemes[currentTheme],
    reloadCustomThemes
  }), [currentTheme, allThemes]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}